import React from "react";
import "./ProgressBar.css";

type ProgressBarProps = {
  className?: string;
  value: number;
  height?: number;
};

/**
 * A determinate progress bar that fills the box it is put in.
 *
 * `.progressBarContainer` declares `width: 100%` and takes its height from the
 * `height` prop (default 8), and its only child is `.progressBar`, whose width
 * is also a percentage (ProgressBar.css:1-13). So the component has no
 * intrinsic width at all: every dimension it has is a percentage of something
 * else. Its one call site gives it that something — `.uploadToastItemProgress`,
 * a 15px-tall row inside the ≤500px upload toast (UploadToast.tsx:105,
 * UploadToast.css).
 *
 * On a bare Storybook canvas there is no such parent. `layout: "centered"`
 * makes #storybook-root a shrink-to-fit flex item, a percentage width
 * contributes nothing to max-content, and the bar resolves to 0 wide — the
 * component renders, and a designer sees an empty panel. The frame is that
 * missing parent, sized like the real one.
 *
 * @story frame 320x16
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({ className, value, height = 8 }) => {
  return (
    <div className={`progressBarContainer ${className}`} style={{ height }}>
      <div
        className={value >= 100 ? "progressBar progressBarComplete" : "progressBar"}
        style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}
      />
    </div>
  );
};
